import { supabase } from '@/lib/supabase';
import type { HabitStatus } from '@/services/records';

export interface StreakData {
  currentStreak: number;
  longestStreak: number;
  currentStreakStart: string;
  longestStreakPeriod: string;
}

export interface CompletionStats {
  weekly: number;
  monthly: number;
  yearly: number;
}

export interface WeeklyBreakdown {
  day: string;
  morningOnTime: number;
  morningLate: number;
  morningMissed: number;
  bedtimeOnTime: number;
  bedtimeLate: number;
  bedtimeMissed: number;
  skipped: number;
}

export interface MonthlyTrend {
  month: string;
  completionRate: number;
  avgDelta: number;
}

export interface DayOfWeekStats {
  day: string;
  successRate: number;
  avgDelta: number;
}

export interface CallStats {
  totalCalls: number;
  answeredRate: number;
  avgDuration: number;
  voicemailRate: number;
  missedCallRate: number;
}

export interface StatsCard {
  label: string;
  value: string;
  icon: string;
  color: string;
  bg: string;
}

export interface Statistics {
  streakData: StreakData;
  completionStats: CompletionStats;
  weeklyBreakdown: WeeklyBreakdown[];
  monthlyTrends: MonthlyTrend[];
  dayOfWeekStats: DayOfWeekStats[];
  callStats: CallStats;
  statsCards: StatsCard[];
}

interface RecordRow {
  record_date: string;
  status: HabitStatus;
  delta_minutes: number | null;
  habit_types: { name: string } | null;
}

const SUCCESS: HabitStatus[] = ['confirmed_early', 'on_time', 'late'];
const SKIP: HabitStatus[] = ['shabbat', 'holiday'];

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fmtDate(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function fmtShort(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** A day counts toward the streak if all records succeeded; shabbat/holiday days are skipped (preserved). */
function dayOutcome(records: RecordRow[]): 'success' | 'skip' | 'fail' {
  const active = records.filter((r) => !SKIP.includes(r.status));
  if (active.length === 0) return 'skip';
  if (active.some((r) => r.status === 'missed')) return 'fail';
  if (active.every((r) => SUCCESS.includes(r.status))) return 'success';
  return 'skip'; // only pending records — day not yet decided
}

function computeStreaks(byDate: Map<string, RecordRow[]>, todayStr: string): StreakData {
  const dates = [...byDate.keys()].sort();
  if (dates.length === 0) {
    return { currentStreak: 0, longestStreak: 0, currentStreakStart: '—', longestStreakPeriod: '—' };
  }

  // Longest streak: walk forward
  let longest = 0;
  let longestStart = '';
  let longestEnd = '';
  let run = 0;
  let runStart = '';
  for (const date of dates) {
    const outcome = dayOutcome(byDate.get(date)!);
    if (outcome === 'success') {
      if (run === 0) runStart = date;
      run += 1;
      if (run > longest) {
        longest = run;
        longestStart = runStart;
        longestEnd = date;
      }
    } else if (outcome === 'fail') {
      run = 0;
    }
    // skip: streak preserved, not incremented
  }

  // Current streak: walk backward from today
  let current = 0;
  let currentStart = '';
  const d = new Date(`${todayStr}T00:00:00`);
  for (let i = 0; i < 400; i++) {
    const key = toDateStr(d);
    const recs = byDate.get(key);
    if (!recs) {
      if (key !== todayStr) break; // gap in history ends the streak (today may not have records yet)
    } else {
      const outcome = dayOutcome(recs);
      if (outcome === 'fail') break;
      if (outcome === 'success') {
        current += 1;
        currentStart = key;
      }
    }
    d.setDate(d.getDate() - 1);
    if (key === dates[0]) break;
  }

  return {
    currentStreak: current,
    longestStreak: Math.max(longest, current),
    currentStreakStart: currentStart ? fmtDate(currentStart) : '—',
    longestStreakPeriod: longestStart ? `${fmtShort(longestStart)} – ${fmtShort(longestEnd)}` : '—',
  };
}

function completionRate(rows: RecordRow[]): number {
  const active = rows.filter((r) => !SKIP.includes(r.status) && r.status !== 'pending');
  if (active.length === 0) return 0;
  const done = active.filter((r) => SUCCESS.includes(r.status)).length;
  return Math.round((done / active.length) * 100);
}

let cache: { userId: string; at: number; data: Statistics } | null = null;
let inflight: Promise<Statistics | null> | null = null;
const CACHE_TTL_MS = 30_000;

/** Fetch & compute all statistics for the current user. Cached briefly so widgets can share. */
export async function fetchStatistics(force = false): Promise<Statistics | null> {
  const { data: { session } } = await supabase.auth.getSession();
  const user = session?.user;
  if (!user) return null;

  if (!force && cache && cache.userId === user.id && Date.now() - cache.at < CACHE_TTL_MS) {
    return cache.data;
  }
  if (!force && inflight) return inflight;

  inflight = (async (): Promise<Statistics | null> => {
    const now = new Date();
    const yearAgo = new Date(now);
    yearAgo.setFullYear(yearAgo.getFullYear() - 1);

    const [recordsRes, callsRes] = await Promise.all([
      supabase
        .from('daily_records')
        .select('record_date, status, delta_minutes, habit_types(name)')
        .eq('user_id', user.id)
        .gte('record_date', toDateStr(yearAgo))
        .order('record_date', { ascending: true }),
      supabase
        .from('phone_calls')
        .select('status, duration_seconds')
        .eq('user_id', user.id),
    ]);

    if (recordsRes.error) {
      console.error('Failed to load records for statistics:', recordsRes.error);
      return null;
    }

    const rows = (recordsRes.data || []) as unknown as RecordRow[];
    const calls = callsRes.data || [];

    const byDate = new Map<string, RecordRow[]>();
    for (const r of rows) {
      const list = byDate.get(r.record_date) || [];
      list.push(r);
      byDate.set(r.record_date, list);
    }

    const todayStr = toDateStr(now);
    const streakData = computeStreaks(byDate, todayStr);

    // Completion rates
    const weekAgo = new Date(now); weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(now); monthAgo.setDate(monthAgo.getDate() - 30);
    const weekRows = rows.filter((r) => r.record_date >= toDateStr(weekAgo));
    const monthRows = rows.filter((r) => r.record_date >= toDateStr(monthAgo));
    const completionStats: CompletionStats = {
      weekly: completionRate(weekRows),
      monthly: completionRate(monthRows),
      yearly: completionRate(rows),
    };

    // Weekly breakdown (last 7 days, grouped by weekday)
    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const weeklyBreakdown: WeeklyBreakdown[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = toDateStr(d);
      const recs = byDate.get(key) || [];
      const entry: WeeklyBreakdown = {
        day: dayLabels[d.getDay()],
        morningOnTime: 0, morningLate: 0, morningMissed: 0,
        bedtimeOnTime: 0, bedtimeLate: 0, bedtimeMissed: 0,
        skipped: 0,
      };
      for (const r of recs) {
        const isMorning = /morning/i.test(r.habit_types?.name || '');
        const isBedtime = /bedtime|night/i.test(r.habit_types?.name || '');
        if (SKIP.includes(r.status)) { entry.skipped += 1; continue; }
        if (isMorning) {
          if (r.status === 'late') entry.morningLate += 1;
          else if (r.status === 'missed') entry.morningMissed += 1;
          else if (SUCCESS.includes(r.status)) entry.morningOnTime += 1;
        } else if (isBedtime) {
          if (r.status === 'late') entry.bedtimeLate += 1;
          else if (r.status === 'missed') entry.bedtimeMissed += 1;
          else if (SUCCESS.includes(r.status)) entry.bedtimeOnTime += 1;
        }
      }
      weeklyBreakdown.push(entry);
    }

    // Monthly trends (last 6 months)
    const monthlyTrends: MonthlyTrend[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const prefix = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const monthRows2 = rows.filter((r) => r.record_date.startsWith(prefix));
      const deltas = monthRows2.map((r) => r.delta_minutes).filter((x): x is number => x != null);
      monthlyTrends.push({
        month: d.toLocaleDateString('en-US', { month: 'short' }),
        completionRate: completionRate(monthRows2),
        avgDelta: deltas.length ? Math.round(deltas.reduce((a, b) => a + b, 0) / deltas.length) : 0,
      });
    }

    // Day-of-week stats (full history)
    const dayOfWeekStats: DayOfWeekStats[] = dayLabels.map((label, dow) => {
      const dowRows = rows.filter((r) => new Date(`${r.record_date}T00:00:00`).getDay() === dow);
      const deltas = dowRows.map((r) => r.delta_minutes).filter((x): x is number => x != null);
      return {
        day: label,
        successRate: completionRate(dowRows),
        avgDelta: deltas.length ? Math.round(deltas.reduce((a, b) => a + b, 0) / deltas.length) : 0,
      };
    });

    // Call stats
    const totalCalls = calls.length;
    const answered = calls.filter((c) => c.status === 'answered').length;
    const voicemail = calls.filter((c) => c.status === 'voicemail').length;
    const missed = calls.filter((c) => c.status === 'no_answer' || c.status === 'busy' || c.status === 'failed').length;
    const durations = calls.map((c) => c.duration_seconds).filter((x): x is number => x != null);
    const callStats: CallStats = {
      totalCalls,
      answeredRate: totalCalls ? Math.round((answered / totalCalls) * 100) : 0,
      avgDuration: durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0,
      voicemailRate: totalCalls ? Math.round((voicemail / totalCalls) * 100) : 0,
      missedCallRate: totalCalls ? Math.round((missed / totalCalls) * 100) : 0,
    };

    // Avg delta across last 30 days
    const monthDeltas = monthRows.map((r) => r.delta_minutes).filter((x): x is number => x != null);
    const avgDelta = monthDeltas.length ? Math.round(monthDeltas.reduce((a, b) => a + b, 0) / monthDeltas.length) : 0;

    const statsCards: StatsCard[] = [
      { label: 'Current Streak', value: `${streakData.currentStreak} days`, icon: 'ri-fire-line', color: 'text-amber-500', bg: 'bg-amber-50' },
      { label: 'Longest Streak', value: `${streakData.longestStreak} days`, icon: 'ri-trophy-line', color: 'text-amber-600', bg: 'bg-amber-50' },
      { label: 'Monthly Rate', value: `${completionStats.monthly}%`, icon: 'ri-pie-chart-line', color: 'text-emerald-500', bg: 'bg-emerald-50' },
      { label: 'Avg Delta', value: `${avgDelta > 0 ? '+' : ''}${avgDelta} min`, icon: 'ri-timer-line', color: 'text-accent-500', bg: 'bg-accent-50' },
      { label: 'Answer Rate', value: `${callStats.answeredRate}%`, icon: 'ri-phone-line', color: 'text-primary-500', bg: 'bg-primary-50' },
      { label: 'Total Calls', value: `${callStats.totalCalls}`, icon: 'ri-phone-fill', color: 'text-primary-600', bg: 'bg-primary-50' },
    ];

    const data: Statistics = {
      streakData,
      completionStats,
      weeklyBreakdown,
      monthlyTrends,
      dayOfWeekStats,
      callStats,
      statsCards,
    };
    cache = { userId: user.id, at: Date.now(), data };
    return data;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}
